// @flow
// ref: https://github.com/mironov/react-redux-loading-bar
import React, { Component } from 'react';
import classNames from 'classnames';
// eslint-disable-next-line
import styles from "./styles.scss";

const UPDATE_TIME = 500;
const MAX_PROGRESS = 99;
const PROGRESS_INCREASE = 20;
const ANIMATION_DURATION = UPDATE_TIME * 2;
const TERMINATING_ANIMATION_DURATION = UPDATE_TIME / 2;

type LoadingStatus = 'hidden' | 'start' | 'running' | 'stop';

type Props = {
  className: string,
  maxProgress: number,
  progressIncrease: number,
  progress?: number
};

type State = {
  percent: number,
  status: LoadingStatus,
};

const initialState: State = {
  percent: 0,
  status: 'start',
};

export class VerticalLoadingBar extends Component<Props, State> {
  static defaultProps = {
    className: '',
    maxProgress: MAX_PROGRESS,
    progressIncrease: PROGRESS_INCREASE,
  };

  state = { ...initialState };

  componentDidMount() {
    this.start();
  }

  componentDidUpdate(prevProps, prevState) {
    const { status } = this.state;
    const { progress } = this.props;

    if(progress !== undefined &&prevProps.progress !== progress){
      this.setState({ percent: progress });
    }
    
    if (prevState.status !== status) {
      if (status === 'start') {
        this.start();
      }

      if (status === 'stop') {
        this.stop();
      }
    }
  }

  componentWillUnmount() {
    this.stop();
    clearInterval(this.progressIntervalId);
    clearTimeout(this.terminatingAnimationTimeoutId);
  }

  reset = () => {
    this.terminatingAnimationTimeoutId = null;
    this.setState(initialState);
  };

  newPercent = (percent, progressIncrease): number => {
    const smoothedProgressIncrease =
      progressIncrease * Math.cos(percent * (Math.PI / 2 / 100));

    return percent + smoothedProgressIncrease;
  };

  simulateProgress = () => {
    if(this.props.progress !== undefined) return;
    this.setState((prevState, { maxProgress, progressIncrease }) => {
      let { percent } = prevState;
      const newPercent = this.newPercent(percent, progressIncrease);

      if (newPercent <= maxProgress) {
        percent = newPercent;
      }

      return { percent };
    });
  };

  start = () => {
    if (this.terminatingAnimationTimeoutId) {
      clearTimeout(this.terminatingAnimationTimeoutId);
      this.reset();
    }

    this.progressIntervalId = setInterval(this.simulateProgress, UPDATE_TIME);
    this.setState({ status: 'running' });
  };

  stop = () => {
    clearInterval(this.progressIntervalId);
    this.progressIntervalId = null;

    const terminatingAnimationDuration = this.isShown()
      ? TERMINATING_ANIMATION_DURATION
      : 0;

    this.terminatingAnimationTimeoutId = setTimeout(
      this.reset,
      terminatingAnimationDuration
    );

    this.setState({ percent: 100 });
  };

  isShown = (): boolean => {
    const { percent } = this.state;
    return percent > 0 && percent <= 100;
  };

  buildStyle = (): Object => {
    const { status, percent } = this.state;
    const animationDuration =
      status === 'stop' ? TERMINATING_ANIMATION_DURATION : ANIMATION_DURATION;

    const tx = 100 - percent;

    const style = {
      transform: `translate3d(0px, ${tx}%, 0px)`,
      msTransform: `translate3d(0px, ${tx}%, 0px)`,
      WebkitTransform: `translate3d(0px, ${tx}%, 0px)`,
      MozTransform: `translate3d(0px, ${tx}%, 0px)`,
      OTransform: `translate3d(0px, ${tx}%, 0px)`,
      transition: `transform ${animationDuration}ms linear 0s`,
      msTransition: `-ms-transform ${animationDuration}ms linear 0s`,
      WebkitTransition: `-webkit-transform ${animationDuration}ms linear 0s`,
      MozTransition: `-moz-transform ${animationDuration}ms linear 0s`,
      OTransition: `-o-transform ${animationDuration}ms linear 0s`,
    };

    if (this.isShown()) {
      style.opacity = '1';
    } else {
      style.opacity = '0';
    }

    return { ...style };
  };

  render() {
    const { status } = this.state;
    const { className } = this.props;
    const loaderClassNames = classNames('loading-bar', className);

    if (status === 'hidden') {
      return <div />;
    }

    return <div style={this.buildStyle()} className={loaderClassNames} />;
  }
}
